import { AiMessageSteps } from "__generated__/graphql"

import {
  asRecord,
  parseJsonIfPossible,
  ROLE_LABELS
} from "./aiStepsDrawer.utils"
import { readCommerceRecords } from "./commerceRunTimeline.utils"
import { explainStepCode } from "./stepCodes.labels"
import {
  ARG_LABELS,
  DELIVERY_ACTION_LABELS,
  DELIVERY_LABELS,
  INTENT_LABELS,
  ROLE_REASON_LABELS,
  SEARCH_STEP_LABELS,
  TOOL_ERROR_LABELS,
  toolLabel
} from "./v2AgentLoop.labels"

/**
 * A v2 turn as a story: what the host prepared, each model call and the tools it chose with what
 * they returned, the reply it wrote, and what the host did with it. Every row keeps its stepId so
 * the panel can jump to the raw step when the one-liner raises a question.
 */

export type LoopLine = {
  stepId: string
  label: string
  detail: string
  durationMs: number
  failed: boolean
}

export type LoopIntent = { label: string; text: string }

export type LoopToolCall = {
  stepId: string
  tool: string
  label: string
  failed: boolean
  /** Refused on purpose by the host (confirmation flow) — not an error to chase. */
  expected: boolean
  durationMs: number
  /** Sentence-shaped arguments (query, reason, summary…) — why the model called it. */
  intents: LoopIntent[]
  /** Short `key: value` arguments. */
  args: string[]
  result: string
  resultLines: string[]
  /** Host steps the tool ran under the hood (RAG, HTTP, MCP). */
  inner: LoopLine[]
}

export type LoopRound = {
  index: number
  stepId: string
  durationMs: number
  failed: boolean
  error: string
  inputTokens: number
  outputTokens: number
  cachedTokens: number
  /** Text the model wrote beside its tool calls; the final JSON is read into `reply`. */
  text: string
  calls: LoopToolCall[]
}

export type LoopReply = {
  stepId: string
  action: string
  reply: string
  dmReply: string
  notes: string
  failed: boolean
  error: string
  durationMs: number
}

export type V2AgentLoop = {
  prepare: LoopLine[]
  rounds: LoopRound[]
  reply: LoopReply | null
  settle: LoopLine[]
  prepareMs: number
  agentMs: number
}

const asText = (value: unknown): string =>
  typeof value === "string" ? value.trim() : ""

const asNumber = (value: unknown): number =>
  typeof value === "number" && Number.isFinite(value) ? value : 0

const stepName = (step: AiMessageSteps): string => String(step.step ?? "")

const isFailed = (step: AiMessageSteps): boolean =>
  String(step.status ?? "") === "FAILED"

const payload = (value: unknown) => asRecord(parseJsonIfPossible(value))

export const clip = (text: string, max = 160): string => {
  const flat = text.replace(/\s+/g, " ").trim()
  return flat.length > max ? `${flat.slice(0, max)}…` : flat
}

const HOST_STEP_LABELS: Record<string, string> = {
  TURN_COORDINATION: "Tur koordinasyonu",
  ROLE_SELECTION: "Rol",
  AGENT_CONTEXT: "Ajana verilenler",
  COMMERCE_MARKETPLACE_PRODUCT_RESOLVE: "İlan eşleştirme",
  COMMERCE_PAGE_PRODUCT_RESOLVE: "Sayfa ürünü",
  GET_QPIEN_SHOP_PRODUCT_DETAILS: "Ürün bilgisi",
  MARKETPLACE_REQUEST: "Ürün bilgisi",
  COMMERCE_MCP_LIST_TOOLS: "Mağaza araçları",
  COMMERCE_MCP_TOOL_CALL: "Ön okuma",
  MARKETPLACE_COMPLIANCE_GATE: "Pazaryeri kuralları",
  DELIVERY_GATE: "Gönderim"
}

const countIn = (text: string, pattern: RegExp): number =>
  Number(pattern.exec(text)?.[1] ?? 0)

function hostDetail(step: AiMessageSteps): string {
  const name = stepName(step)
  const request = payload(step.request)
  const response = payload(step.response)
  const summary = asText(step.summary)

  switch (name) {
    case "ROLE_SELECTION": {
      const role = asText(response?.role)
      const reason = asText(response?.reason)
      return `${ROLE_LABELS[role] ?? role} rolü cevaplayacak — ${
        ROLE_REASON_LABELS[reason] ?? reason
      }`
    }
    case "AGENT_CONTEXT": {
      const messageIds = request?.messageIds
      const messages = Array.isArray(messageIds)
        ? messageIds.length
        : countIn(summary, /(\d+) message/)
      const guidance = countIn(summary, /(\d+) guidance/)
      const tools = countIn(summary, /(\d+) tool/)
      return `${messages} müşteri mesajı, ${guidance} rehberlik kuralı ve ${tools} araç ajana verildi`
    }
    case "COMMERCE_MCP_LIST_TOOLS":
      return `mağazadan ${asNumber(response?.count)} araç alındı`
    case "COMMERCE_MARKETPLACE_PRODUCT_RESOLVE": {
      if (response?.matched !== true) {
        const reason = asText(response?.reason)
        const explained = reason ? explainStepCode(name, reason) : null
        if (explained) return `${explained} (${reason})`
        return `sorulan ilan katalogda bulunamadı${reason ? ` (${reason})` : ""}`
      }
      return response?.sellable === false
        ? "sorulan ilan katalogda bulundu ama satışta değil"
        : "sorulan ilan katalogda bulundu"
    }
    case "COMMERCE_PAGE_PRODUCT_RESOLVE":
      return response?.matched === true
        ? "müşterinin açık olan sayfasındaki ürün eşleşti"
        : "açık sayfadaki ürün eşleşmedi"
    case "GET_QPIEN_SHOP_PRODUCT_DETAILS":
      return "ürün bilgisi Qpien Shop kataloğundan okundu"
    case "MARKETPLACE_REQUEST":
      return "ürün bilgisi canlı pazaryeri API'sinden alındı (katalogda yoktu)"
    case "COMMERCE_MCP_TOOL_CALL":
      return `${toolLabel(asText(request?.name))} — ajan başlamadan önce çekildi`
    case "MARKETPLACE_COMPLIANCE_GATE": {
      const raw = response?.violations
      const violations = Array.isArray(raw)
        ? raw.map((v: unknown) => scalar(v) || JSON.stringify(v))
        : []
      if (response?.blocked === true) {
        return `cevap pazaryeri kurallarına takıldı, gönderilmedi: ${violations.join(", ")}`
      }
      return violations.length
        ? `uyarı: ${violations.join(", ")}`
        : "cevap pazaryeri kurallarına uygun"
    }
    case "DELIVERY_GATE": {
      const code = asText(response?.code)
      const raw = response?.actions
      const actions = Array.isArray(raw) ? raw.map(asText).filter(Boolean) : []
      const done = actions.map((a) => DELIVERY_ACTION_LABELS[a] ?? a)
      return [DELIVERY_LABELS[code] ?? (code || summary), done.join(", ")]
        .filter(Boolean)
        .join(" · ")
    }
    case "HTTP_REQUEST":
      return [asText(request?.method), asText(request?.url)]
        .filter(Boolean)
        .join(" ")
    default:
      return summary || asText(step.reason)
  }
}

function hostLine(step: AiMessageSteps): LoopLine {
  const name = stepName(step)
  return {
    stepId: String(step._id),
    label: HOST_STEP_LABELS[name] ?? SEARCH_STEP_LABELS[name] ?? name,
    detail: clip(hostDetail(step), 220),
    durationMs: step.durationMs ?? 0,
    failed: isFailed(step)
  }
}

const scalar = (value: unknown): string => {
  if (typeof value === "string") return value.trim()
  if (typeof value === "number" || typeof value === "boolean") {
    return String(value)
  }
  return ""
}

/** skill ids, step ids and `cap-2-1` style references, named by whatever the run returned for them. */
export type References = Map<string, string>

export function collectReferences(list: AiMessageSteps[]): References {
  const refs: References = new Map()
  const addCapabilities = (value: unknown) => {
    const capabilities = asRecord(value)?.capabilities
    if (!Array.isArray(capabilities)) return
    capabilities.forEach((entry) => {
      const cap = asRecord(entry)
      const id = asText(cap?.referenceId)
      if (id && asText(cap?.label)) refs.set(id, asText(cap?.label))
    })
  }

  list
    .filter((step) => stepName(step) === "AGENT_TOOL_CALL")
    .forEach((step) => {
      const res = payload(step.response)
      if (!res) return
      const skill = asRecord(res.skill)
      if (asText(skill?.id)) refs.set(asText(skill?.id), asText(skill?.name))
      if (Array.isArray(res.steps)) {
        res.steps.forEach((entry) => {
          const item = asRecord(entry)
          const id = asText(item?.stepId)
          if (id && asText(item?.title)) refs.set(id, asText(item?.title))
        })
      }
      addCapabilities(res.activeStep)
      addCapabilities(res.nextStep)
    })
  return refs
}

/** `parameters: [{name, value}]` is how skill/channel actions pass their fields. */
export function readArguments(
  raw: unknown,
  refs: References
): { intents: LoopIntent[]; args: string[] } {
  const args = asRecord(parseJsonIfPossible(raw))
  const intents: LoopIntent[] = []
  const pairs: string[] = []
  if (!args) return { intents, args: pairs }

  const named = (value: string) => refs.get(value) ?? value

  for (const [key, value] of Object.entries(args)) {
    const label = ARG_LABELS[key] ?? key
    if (INTENT_LABELS[key]) {
      const text = scalar(value)
      if (text) intents.push({ label: INTENT_LABELS[key], text })
      continue
    }
    if (key === "parameters" && Array.isArray(value)) {
      value.forEach((entry) => {
        const field = asRecord(entry)
        const fieldName = asText(field?.name)
        const fieldValue = scalar(field?.value)
        if (fieldName && fieldValue) pairs.push(`${fieldName}: ${fieldValue}`)
      })
      continue
    }
    if (Array.isArray(value)) {
      const items = value.map(scalar).filter(Boolean).map(named)
      if (items.length) pairs.push(`${label}: ${items.join(", ")}`)
      continue
    }
    const text = scalar(value)
    if (text) pairs.push(`${label}: ${clip(named(text), 60)}`)
    else if (value && typeof value === "object") {
      pairs.push(`${label}: ${clip(JSON.stringify(value), 60)}`)
    }
  }
  return { intents, args: pairs }
}

const titled = (value: unknown): string => asText(asRecord(value)?.title)

/** One headline and a few lines saying what the tool handed back, per tool family. */
export function summarizeToolResult(
  tool: string,
  raw: unknown,
  reason: string
): { result: string; lines: string[] } {
  const res = asRecord(parseJsonIfPossible(raw))
  if (!res) return { result: clip(scalar(raw)), lines: [] }

  if (res.ok === false || res.status === "error") {
    const code = asText(res.error) || reason || "hata"
    const params = asRecord(res.parameters)
    return {
      result: TOOL_ERROR_LABELS[code]?.text ?? `hata · ${code}`,
      lines: params
        ? [
            Object.entries(params)
              .map(([k, v]) => `${k}: ${scalar(v)}`)
              .join(" · ")
          ]
        : []
    }
  }

  if (res.status === "duplicate") {
    return { result: "aynı sorgu bu turda zaten arandı", lines: [] }
  }
  if (res.status === "budget_exhausted") {
    return { result: "bilgi arama bütçesi bitti", lines: [] }
  }

  if (tool === "search_knowledge") {
    const results = Array.isArray(res.results) ? res.results : []
    const status = asText(res.status)
    return {
      result:
        results.length > 0
          ? `${results.length} bilgi parçası bulundu`
          : status === "disabled"
            ? "bilgi tabanı kapalı"
            : "sonuç yok",
      lines: results.slice(0, 3).map((entry) => {
        const item = asRecord(entry)
        const title = asText(item?.title)
        const excerpt = clip(asText(item?.excerpt), 120)
        return title ? `${title} — ${excerpt}` : excerpt
      })
    }
  }

  if (tool === "skill_knowledge") {
    const chunks = Array.isArray(res.chunks) ? res.chunks.map(asText) : []
    const found = asNumber(res.found) || chunks.length
    const source = asText(res.source)
    return {
      result: found
        ? `${found} bilgi parçası bulundu${source ? ` · kaynak: ${source}` : ""}`
        : "sonuç yok",
      lines: chunks.slice(0, 2).map((chunk) => clip(chunk, 140))
    }
  }

  const skill = asRecord(res.skill)
  if (skill) {
    const steps = Array.isArray(res.steps) ? res.steps.length : 0
    const active = titled(res.activeStep)
    return {
      result: `Skill başladı: ${asText(skill.name)}`,
      lines: [
        [steps ? `${steps} adımlı` : "", active ? `ilk adım: ${active}` : ""]
          .filter(Boolean)
          .join(" · ")
      ].filter(Boolean)
    }
  }

  if ("nextStep" in res || "completed" in res) {
    const next = titled(res.nextStep)
    return {
      result:
        res.completed === true
          ? "skill'in tüm adımları tamamlandı"
          : next
            ? `adım tamamlandı → sıradaki adım: ${next}`
            : "tamam",
      lines: []
    }
  }

  const records = readCommerceRecords(res)
  if (records.length) {
    return {
      result: `${records.length} kayıt döndü`,
      lines: records
        .slice(0, 4)
        .map((record) =>
          [record.title, record.price, record.availability]
            .filter(Boolean)
            .join(" · ")
        )
    }
  }

  const data = res.data ?? res.result
  if (data !== undefined) {
    const text = typeof data === "string" ? data : JSON.stringify(data)
    return { result: "action veri döndürdü", lines: [clip(text, 180)] }
  }

  const productTitle = asText(res.title)
  if (productTitle) return { result: productTitle, lines: [] }

  const keys = Object.keys(res).filter((key) => key !== "ok")
  return {
    result: keys.length ? `döndü (${keys.join(", ")})` : "tamam",
    lines: []
  }
}

type OutputItem = {
  type?: string
  name?: string
  content?: unknown
}

export function readModelText(output: unknown): string {
  if (!Array.isArray(output)) return ""
  return (output as OutputItem[])
    .filter((item) => item?.type === "message")
    .flatMap((item) =>
      Array.isArray(item.content)
        ? (item.content as unknown[]).map((part) =>
            asText(asRecord(part)?.text)
          )
        : []
    )
    .filter(Boolean)
    .join("\n")
}

function readRound(step: AiMessageSteps, index: number): LoopRound {
  const response = payload(step.response)
  const usage = asRecord(response?.usage)
  const text = readModelText(response?.output)
  const structured = asRecord(parseJsonIfPossible(text))

  return {
    index,
    stepId: String(step._id),
    durationMs: step.durationMs ?? 0,
    failed: isFailed(step),
    error:
      asText(response?.error) || (isFailed(step) ? asText(step.reason) : ""),
    inputTokens: asNumber(usage?.inputToken ?? usage?.inputTokens),
    outputTokens: asNumber(usage?.outputToken ?? usage?.outputTokens),
    cachedTokens: asNumber(usage?.cachedToken),
    text: structured ? "" : clip(text, 400),
    calls: []
  }
}

function readToolCall(step: AiMessageSteps, refs: References): LoopToolCall {
  const request = payload(step.request)
  const tool = asText(request?.tool) || "tool"
  const { intents, args } = readArguments(request?.arguments, refs)
  const code = asText(payload(step.response)?.error) || asText(step.reason)
  const { result, lines } = summarizeToolResult(
    tool,
    step.response,
    asText(step.reason)
  )
  return {
    stepId: String(step._id),
    tool,
    label: toolLabel(tool),
    failed: isFailed(step),
    expected: isFailed(step) && TOOL_ERROR_LABELS[code]?.expected === true,
    durationMs: step.durationMs ?? 0,
    intents,
    args,
    result,
    resultLines: lines,
    inner: []
  }
}

function readReply(step: AiMessageSteps): LoopReply {
  const response = payload(step.response)
  const output = asRecord(response?.output)
  return {
    stepId: String(step._id),
    action: asText(output?.action),
    reply: asText(output?.reply),
    dmReply: asText(output?.dmReply),
    notes: asText(output?.notes),
    failed: isFailed(step),
    error:
      asText(response?.error) || (isFailed(step) ? asText(step.reason) : ""),
    durationMs: step.durationMs ?? 0
  }
}

export function buildV2AgentLoop(
  steps: AiMessageSteps[] | undefined | null
): V2AgentLoop | null {
  const list = [...(steps ?? [])].sort(
    (a, b) => (a.stepList ?? 0) - (b.stepList ?? 0)
  )
  if (!list.some((step) => stepName(step) === "AGENT_MODEL_CALL")) return null
  const refs = collectReferences(list)

  const loop: V2AgentLoop = {
    prepare: [],
    rounds: [],
    reply: null,
    settle: [],
    prepareMs: 0,
    agentMs: 0
  }
  let phase: "prepare" | "agent" | "settle" = "prepare"

  for (const step of list) {
    const name = stepName(step)

    if (name === "AGENT_MODEL_CALL") {
      phase = "agent"
      loop.rounds.push(readRound(step, loop.rounds.length + 1))
      continue
    }
    if (name === "AGENT_OUTPUT") {
      phase = "settle"
      loop.reply = readReply(step)
      loop.agentMs = step.durationMs ?? 0
      continue
    }

    const round = loop.rounds[loop.rounds.length - 1]

    if (phase === "agent" && name === "AGENT_TOOL_CALL" && round) {
      round.calls.push(readToolCall(step, refs))
      continue
    }

    if (phase === "prepare") {
      loop.prepare.push(hostLine(step))
      loop.prepareMs += step.durationMs ?? 0
      continue
    }
    if (phase === "settle") {
      loop.settle.push(hostLine(step))
      continue
    }

    const call = round?.calls[round.calls.length - 1]
    if (call) call.inner.push(hostLine(step))
  }

  if (!loop.agentMs) {
    loop.agentMs = loop.rounds.reduce(
      (total, round) =>
        total +
        round.durationMs +
        round.calls.reduce((sum, call) => sum + call.durationMs, 0),
      0
    )
  }

  return loop
}
